import React from 'react';
import moment from 'moment'
import _ from 'lodash'

import Grid from '@material-ui/core/Grid';

import {buildQuestionUIDForMember, Service} from "./service";
import {
  ACCOUNT_HOLDER_1_UID,
  ACCOUNT_HOLDER_2_UID,
  finalizeStepsData,
  INTERVIEW_PLACE_ACCOUNT_HOLDER_1_UID, INTERVIEW_PLACE_ACCOUNT_HOLDER_2_UID, INTERVIEW_PLACE_CUSTOMER_INPUT_UID
} from "./mock_finalize"
import {
  getInvestorProfile, getMemberFieldName,
  getPermanentSuitabilityCheckQuestionChoices, isIndividualAssetManagementBank, setQuestionAnswerFromStore,
} from "./utils";
import {
  CustomerResource,
  ModelPortfolioResource,
  QuestionnairesHandlerResource
} from '../../utils/api'
import {SERVICE_CONCEPTS} from "../Trades/constants";
import {CONGRATS_STEP_ID, CUSTOMER_TYPES, radioNoUID, radioYesUID} from "./constants";


export class FinalizeService extends Service {
  constructor(uid, name, next_btn, success_text, getPrevStepAnswer, createAccountFunc) {
    super(uid, name, next_btn, success_text, getPrevStepAnswer);

    this._createAccountFunc = createAccountFunc;
    this._last_risk_profile = null
    this.__lastFinsihedProcess = null;

    this._instruments_info = [];
    this.__stepsData = finalizeStepsData;
    this._createAccountStep = 'finalize-conversation-details';
  }

  get alternativeFundsInvested() {
    let instruments = [...this.investedInstrumentsInfo]

    let is_afmid = false

    instruments.forEach(instrument => {
      if (!is_afmid) {
        is_afmid = instrument.is_aifmd || false
      }
    })

    return is_afmid
  }

  get investedInstrumentsInfo() {
    return this._instruments_info
  }

  get investedInstrumentsGroupedByCustodian() {
    return {
      [_.get(this.custodian, 'depot.bankCompanyId')]: {
        instruments: this._instruments_info,
        hasEinzeltitels: _.some(this._instruments_info, (instrument) => !instrument.is_fund),
        einzeltitelsSubmissionMessage: _.get(this.custodian, 'contract.configuration.einzeltitel_submission_message')
      }
    }
  }

  get investedInstruments() {

    const modelPortfolio = this._getPrevStepAnswer('products_selection', 'product-selection-investment-recommendation', 'instruments_source');
    if (_.get(modelPortfolio, 'isInvestmentStrategy')) {
      return []
    }

    let answer = this._getPrevStepAnswer('products_selection', 'product-selection-investment-recommendation', "instruments", true)
    return answer || []
  }

  get isIndividualAssetManagementWithExistingDepot(){
    return this._getPrevStepAnswer('products_selection', 'existing-depot-step', 'has_existing_depot') === radioYesUID;
  }

  async __sendStepData() {

    if(this._step && this._step.uid === this._createAccountStep){
      // We can call create-order end point many times.
      // Only one business case will be created with only one online document (latest created) inside.
      if (typeof this._createAccountFunc === 'function'){
        await this._createAccountFunc();
      }
    }

    // Protocol Step for Buy/Sell
    const isFinished = this._step && this._step.uid === 'finalize-documents-downloading';

    return await super.__sendStepData(isFinished);
  }

  async getInstrumentsInfo() {
    let isins = this.investedInstruments.map(instrument => instrument.isin)
    isins = _.filter(isins, isin => !!isin)
    isins = Array.from(new Set(isins))

    if (isins.length) {
      this._instruments_info = await ModelPortfolioResource.infoAssets(isins)
    }
  }

  async getCustomerContactBroker() {
    try {
      return await CustomerResource.at(`${this.customer_id}/contact-broker/`).get()
    } catch (error) {
      // TODO: Prepare better way to handle cases,
      //  when customer does not have contact broker
      console.log(error)
      return {}
    }
  }

  /**
   * Get last Risk Profile for the customer.
   * */
  async getLastRiskProfile() {
    try {
      let { onboarding, questionnaire} = await QuestionnairesHandlerResource.getLastFinishedRiskProfile(this.customer_id, true)

      if (onboarding) {
        this._last_risk_profile = onboarding
      }
    } catch (error) {
      // not critical as we use it just to pre-fill
    }
  }

  async getLastProcess(){
    try {
      let { onboarding } = await QuestionnairesHandlerResource.getLastFinishedRiskProfile(this.customer_id, false, false, true);

      if (onboarding) {
        this.__lastFinsihedProcess = onboarding
      }
    } catch (error) {
      // not critical as we use it just to pre-fill
    }
  }

  _setDefaultAnswers(onboardingAnswers) {
    super._setDefaultAnswers(onboardingAnswers);

    let permanentSuitabilityCheckQuestion = this.getQuestion(
      !this.isNewDesign ? 'finalize-conversation-details' : 'finalize-sustainability-check', 'permanent_suitability_check');

    if (permanentSuitabilityCheckQuestion) {
      if(!this.isNewDesign) {
        permanentSuitabilityCheckQuestion.config.choices = getPermanentSuitabilityCheckQuestionChoices(!this.useMembers)
      }
      if(isIndividualAssetManagementBank(this, _.get(this.bank, 'custodian_id'))){
        permanentSuitabilityCheckQuestion.answer = radioYesUID;
        permanentSuitabilityCheckQuestion.config.choices = permanentSuitabilityCheckQuestion.config.choices.filter(c => c.uid === radioYesUID);
      } else if (!_.isNil(this.current_customer) && !_.isNil(this.current_customer.permanent_suitability_check)) {
        permanentSuitabilityCheckQuestion.answer = this.current_customer.permanent_suitability_check ? radioYesUID : radioNoUID
      }
    }

  }

  getDataForAccount(validate){
    // Function that returns data for complex assets if it is required

    let bodyWithAccountDetails = {allow_complex_funds: false};

    if(!this.isSellOnlyFlow && this.serviceConcept != SERVICE_CONCEPTS.ExecutionOnly){
      const containsComplexInstrument = () => {
        // Function that check if there are any complex instruments in 'buy-containing' process
        let transactionsOfPortfolios = this._getPrevStepAnswer('trade', 'trade-step', "transactions", true) || []

        return transactionsOfPortfolios.some(portfolioTransactionsData => {

            let buyTransactionsList = [];
            // check instruments of 'buy containing' processes
            ['buy', 'savings_plan', 'switch_plan', 'switch'].map(transactionKey => {

              let transactions = _.get(portfolioTransactionsData.transactions, transactionKey, [])

              if (transactionKey === 'savings_plan'){
                 // for savings_plan only transactions with 'create' action should be taken into account
                transactions = transactions.filter(transaction => ['create', 'edit'].includes(transaction.action))
              } else if (['switch_plan', 'switch'].includes(transactionKey)){
                // for switch or switch_plan only buy transactions should be taken into account
                transactions = _.flatten(transactions.map(switchTransaction => switchTransaction.buy))
              }

              buyTransactionsList = buyTransactionsList.concat(transactions)
            })

            return buyTransactionsList.some(t => t.data.is_aifmd)
          })
      }

      // if onboarding - form is attached in any case, if trading - form is attached if there are complexInstruments
      if (!this.is_trading || containsComplexInstrument()){
        bodyWithAccountDetails = {
          allow_complex_funds: true,
          investor_profile: getInvestorProfile(this.serviceConcept, this._getPrevStepAnswer)
        }
      }

    }

    const signaturePlaces = {
      advisor: this.getStepAnswer('finalize-conversation-details', 'interview_place_broker_input') || undefined,
    };
    const accountHoldersUids = this.useMembers && !_.isEmpty(this.members)
      ? [INTERVIEW_PLACE_ACCOUNT_HOLDER_1_UID, INTERVIEW_PLACE_ACCOUNT_HOLDER_2_UID]
      : [INTERVIEW_PLACE_CUSTOMER_INPUT_UID];
    accountHoldersUids.forEach((uid, idx) => {
      signaturePlaces[`${getMemberFieldName(this.customer_type)}_${idx + 1}`] = this.getStepAnswer(
        'finalize-conversation-details', uid) || undefined;
    });

    return {
      account_details: bodyWithAccountDetails,
      signature_places: signaturePlaces
    }
  }

  allInstrumentsAreNonFondsOrPi(){

    if (this.is_trading){
      let response = true
      let onlyPi = true
      let onlyEinzeltitle = true
      this.investedDepots.map(depot => {
        onlyPi = onlyPi && depot.data.isPrivateInvestment;

        // if onlyPi - it is portfolio, otherwise check if asset is not fund
        if(!onlyPi){
         onlyEinzeltitle = onlyEinzeltitle && _.every(_.flatten(Object.values(depot.transactions)), (instrument) => !instrument.data.is_fund);
        }

        if (!onlyPi && !onlyEinzeltitle) {
          response = false;
          return // instead of break
        }
      })

      return response
    } else{

      let instrumentsSource = this._getPrevStepAnswer('products_selection', 'product-selection-investment-recommendation', 'instruments_source', true);
      return _.get(instrumentsSource, 'isInvestmentStrategy') || _.every(this.investedInstruments, (instrument) => !instrument.is_fund)
    }
  }

  async __setQuestionnaire(onboardingAnswers) {
    this._questionnaire = this.__deepCopy(this.__stepsData);

    await Promise.all([
      this._getBankDetails(),
      this.getLastRiskProfile(),
      this.getLastProcess(),
      this.getInstrumentsInfo()])

    this.contactBroker = await this.getCustomerContactBroker()

    this._setDefaultAnswers(onboardingAnswers);

    let finalizeSellsStep = this.getStep('finalize-sells');
    if(this.is_couple){
      finalizeSellsStep.info = 'Bitte achten Sie bei Gemeinschaftsdepots darauf, dass die Unterlagen von allen Depotinhabern unterschrieben werden.';
    }

    let alternativeFundsQuestion = finalizeSellsStep.question.find(question => question.uid == "yearly_report_information_provided['checkbox']");
    let mainInfoProvidedQuestion = finalizeSellsStep.question.find(question => question.uid == "main_information_provided['checkbox']");

    const isIndividualAssetManagementProcess = isIndividualAssetManagementBank(this, _.get(this.bank, 'custodian_id'));
    if(isIndividualAssetManagementProcess){
      finalizeSellsStep.name = 'Angaben zu Interessenskonflikten'
      if (alternativeFundsQuestion) {
        alternativeFundsQuestion._ignore = true;
        this._setQuestionOptionalFlag(alternativeFundsQuestion, true)
      }
      if (mainInfoProvidedQuestion) {
        mainInfoProvidedQuestion._ignore = true;
        this._setQuestionOptionalFlag(mainInfoProvidedQuestion, true);
      }
      const riskQ = this.getQuestion('finalize-risk', 'risk');
      if(riskQ){
        riskQ.question_text = 'Der Kunde wurde über die mit der Anlagestrategie sowie die mit den zum Einsatz ' +
          'zugelassenen Finanzinstrumenten in Verbindung stehenden Risiken aufgeklärt. Insbesondere wurde der Kunde ' +
          'darauf hingewiesen, dass das Risiko eines Totalverlustes der investierten Vermögenswerte besteht.'
      }

      let protocolClientQuestion = this.getQuestion('finalize-client', 'data_type');
      if(protocolClientQuestion){
        protocolClientQuestion.config.choices[0].text =
          "Die im Risikoprofil nach § 63 Abs. 10 WpHG bzw. § 16 Abs. 1 FinVermV enthaltenen Informationen über die " +
          "Kenntnisse und Erfahrungen, Anlageziele und finanzielle Verhältnisse des Kunden gemäß der Kundenangaben " +
          "vom <data_type_date> wurden auf ihre Aktualität hin überprüft, der Geeignetheitserklärung zugrunde gelegt " +
          "und mit dieser ausgehändigt."

        protocolClientQuestion.config.choices[1].text =
          "Das Risikoprofil wurde neu erhoben und wird dem Kunden mit der Geeignetheitserklärung ausgehändigt."
      }
    } else {
      if (alternativeFundsQuestion && this.alternativeFundsInvested) {
        this._setQuestionOptionalFlag(alternativeFundsQuestion, false)
      }
      if (mainInfoProvidedQuestion && this.allInstrumentsAreNonFondsOrPi()) {
        this._setQuestionOptionalFlag(mainInfoProvidedQuestion, true);
      }
    }

    let protocolClientDateQuestion = this.getQuestion('finalize-client', 'data_type_date');
    if (protocolClientDateQuestion && this._last_risk_profile && !protocolClientDateQuestion.answer) {
      protocolClientDateQuestion.answer = moment(this._last_risk_profile.updated_at)
    }

    const conversationDetailsStep = this.getStep('finalize-conversation-details');
    const conversationDateAndPlace = this.isNewDesign ? this.getStep('finalize-conversation-details-date-place') : conversationDetailsStep;
    const conversationParticipants = this.isNewDesign ? this.getStep('finalize-conversation-details-participants') : conversationDetailsStep;
    let brokerDetailsQuestion = conversationParticipants.question.find(question => question.uid == "interview_broker_input");
    let brokerInterviewPlaceQuestion = conversationDateAndPlace.question.find(question => question.uid == "interview_place_broker_input");
    brokerDetailsQuestion.answer = `${_.get(this.contactBroker, 'broker_first_name', '')} ${_.get(this.contactBroker, 'broker_last_name', '')}`
    brokerInterviewPlaceQuestion.defaultAnswer = _.get(this.contactBroker, 'agency.city');
    const customerInterviewPlaceQuestion = conversationDateAndPlace.question.find(question => question.uid == INTERVIEW_PLACE_CUSTOMER_INPUT_UID)
    if(this.useMembers && !_.isEmpty(this.members)) {
      customerInterviewPlaceQuestion._ignore = true;
      [[ACCOUNT_HOLDER_1_UID, INTERVIEW_PLACE_ACCOUNT_HOLDER_1_UID], [ACCOUNT_HOLDER_2_UID, INTERVIEW_PLACE_ACCOUNT_HOLDER_2_UID]].map((uids, idx) => {
        const [uid, interviewPlaceUid] = uids;
        let accountHolderQuestion = conversationParticipants.question.find(q => q.uid === uid);
        let accountHolderInterviewPlaceQuestion = conversationDateAndPlace.question.find(q => q.uid === interviewPlaceUid);
        accountHolderInterviewPlaceQuestion.defaultAnswer = this.getMemberRegistrationAddress(idx);
        if(!this.members[idx]) {
          return;
        }
        accountHolderQuestion._ignore = false;
        accountHolderInterviewPlaceQuestion._ignore = false;
        if (this.customer_type === CUSTOMER_TYPES.MINOR) {
          accountHolderQuestion.uid = buildQuestionUIDForMember('interview_customer_input', idx, this.customer_type);
          accountHolderQuestion.question_text = `${this.isNewDesign ? 'Erziehungsberechtigte/-r' : 'Erziehungsberechtigter'} ${idx + 1}`;
          accountHolderInterviewPlaceQuestion.question_text = `${this.isNewDesign ? 'Ort des Erziehungsberechtigten' : 'Erziehungsberechtigter'} ${idx + 1}`;
        }
        this._setQuestionOptionalFlag(accountHolderQuestion, !!idx);
        this._setQuestionOptionalFlag(accountHolderInterviewPlaceQuestion, !!idx);

        if(!accountHolderQuestion.answer) {
          accountHolderQuestion.answer = `${this.getMemberFullName(idx)}`;
        }
      });
    } else {
      let customerDetailsQuestion = conversationParticipants.question.find(question => question.uid == "interview_customer_input")
      customerDetailsQuestion.answer = `${this.getMemberFullName()}`;
      this._setQuestionOptionalFlag(customerDetailsQuestion, false);
      this._setQuestionOptionalFlag(customerInterviewPlaceQuestion, false);
      customerInterviewPlaceQuestion.defaultAnswer = this.getMemberRegistrationAddress();
    }
    let interviewDateQuestion = conversationDateAndPlace.question.find(question => question.uid == "interview_date");
    if (!interviewDateQuestion.answer) {
      interviewDateQuestion.answer = moment()
    }

    let interviewRepeatedDateQuestion = conversationParticipants.question.find(question => question.uid == "interview_repeated_date");
    if (interviewRepeatedDateQuestion && this.__lastFinsihedProcess && !interviewRepeatedDateQuestion.answer) {
      const lastInterviewDateQuestion = _.get(this.__lastFinsihedProcess, `onboarding_answers.protocol.${conversationParticipants.uid}`, []).find(q => q.question_uid === 'interview_date');

      if(lastInterviewDateQuestion && !_.isEmpty(lastInterviewDateQuestion.answer)){
        setQuestionAnswerFromStore(interviewRepeatedDateQuestion, lastInterviewDateQuestion);
      }
    }

    if(this.isNewDesign && isIndividualAssetManagementProcess){
      this._questionnaire.steps = _.filter(this._questionnaire.steps, (step) => step.uid !== 'finalize-sells')
    }

    if (this.serviceConcept == SERVICE_CONCEPTS.ExecutionOnly) {
      this._questionnaire.steps = _.filter(this._questionnaire.steps, (step) => !['finalize-sells', 'finalize-sells-conflicts', 'finalize-risk', 'finalize-client', 'finalize-conversation-details-reason'].includes(step.uid))
    }

    if (this.serviceConcept != SERVICE_CONCEPTS.Anlageberatung) {
      if(this.isNewDesign){
        this._questionnaire.steps = _.filter(this._questionnaire.steps, (step) => step.uid !== 'finalize-sustainability-check')
      } else {
        let permanentSuitabilityCheckQuestion = conversationDetailsStep.question.find(q => q.uid == "permanent_suitability_check");
        permanentSuitabilityCheckQuestion.optional = true
      }
    }

    const reasonStep = this.isNewDesign ? this.getStep('finalize-conversation-details-reason') : conversationDetailsStep;

    if(reasonStep) {
      const reasonsQuestion = reasonStep.question.find(q => q.uid == "interview_reasons");

      if(isIndividualAssetManagementProcess && reasonsQuestion){
        reasonsQuestion.question_text = 'Individuelle Begründung der Strategieempfehlung';
        reasonsQuestion.info_text = <IndividualInfoBlock />
      }

      // in case interview_reasons was stored it should not be overtaken anymore
      if (!reasonsQuestion.answer && !_.get(onboardingAnswers, [this.uid, reasonStep.uid], []).find(q => q.question_uid === reasonsQuestion.uid)) {
        reasonsQuestion.answer = this.portfolioBuilderReason || undefined;
      }
    }
  }

  async confirmResult(data) {
    this._is_finished = true;
  }

  get portfolioBuilderReason(){
    const ptfBuilderAnswer = this._getPrevStepAnswer('products_selection', 'product-selection-investment-recommendation', "portfolio_builder_case");
    return _.get(ptfBuilderAnswer, 'justification');
  }

  get hideSideMenu(){
    return this.step.uid === CONGRATS_STEP_ID;
  }
}

const IndividualInfoBlock = () => (
  <Grid container spacing={1}>
    <Grid item xs={12}>&nbsp;</Grid>
    <Grid item xs={12}><b>Hinweis</b></Grid>
    <Grid item xs={12}>
      <i>
        Ich erbringe als vertraglich gebundener Vermittler im Haftungsdach der BfV AG die Anlageberatung und
        Anlagevermittlung in Bezug auf Aktien, Schuldtitel, Anteile an Investmentvermögen gemäß § 1 Abs. 2 KAGB,
        Geldmarktinstrumente und Derivate gegenüber meinen Kunden.
      </i>
    </Grid>
    <Grid item xs={12}>
      <i>
        Die Finanzportfolioverwaltung / individuelle Vermögensverwaltung ist nicht von der Anbindung als vertraglich
        gebundener Vermittler umfasst.
      </i>
    </Grid>
    <Grid item xs={12}>
      <i>
        Hierzu zählen insbesondere die Überwachung der Performance des Portfolios des Kunden sowie die Umsetzung von
        Anlageentscheidungen durch Erteilung entsprechender Anweisung an die BfV AG als Vermögensverwalter bzw. an die Depotbank.
      </i>
    </Grid>
  </Grid>
)